import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { eatDayOf, formatEatTime, formatIsoDay, INCIDENT_TEXT_LIMITS } from '@wodi/domain';
import {
  Button,
  Checkbox,
  DateField,
  LoadingBlock,
  Notice,
  PageHeader,
  Section,
  Select,
  TextArea,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import { incidentKeys, incidentOptionsQuery, reportIncident } from '../api/incidents.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { eatInstant } from '../imaging/format.ts';
import { errorText, LoadFailed, NotAllowed } from '../operations/format.tsx';
import styles from '../operations/operations.module.css';
import { PatientLink, type LinkedPatient } from '../operations/patient-link.tsx';
import { toasts } from '../toasts.ts';

// Report an incident (docs/plans/phase3-operations-modules.md §1.4): any member of staff.
// One form: what, how bad, when and where, what happened and what was done, and optionally
// the patient and the equipment. "Hide my name from colleagues" keeps the name from everyone
// but the safety lead, and the form says so.

const L = INCIDENT_TEXT_LIMITS;

function ReportForm({ mayLinkPatient }: { mayLinkPatient: boolean }) {
  const online = useOnline();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const options = useQuery(incidentOptionsQuery);
  const [now] = useState(() => new Date());
  const [clientRequestId] = useState(() => crypto.randomUUID());
  const [category, setCategory] = useState<string | null>(null);
  const [severity, setSeverity] = useState<string | null>(null);
  const [day, setDay] = useState<string | null>(formatIsoDay(eatDayOf(now)));
  const [time, setTime] = useState(formatEatTime(now));
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [immediateAction, setImmediateAction] = useState('');
  const [equipment, setEquipment] = useState('');
  const [patient, setPatient] = useState<LinkedPatient | null>(null);
  const [visitId, setVisitId] = useState<string | null>(null);
  const [anonymous, setAnonymous] = useState(false);
  const [tried, setTried] = useState(false);

  const at = day === null ? null : eatInstant(day, time);
  const problems = {
    category: category === null ? 'Choose a category.' : null,
    severity: severity === null ? 'Choose how much harm was done.' : null,
    at:
      at === null
        ? 'Enter the day and the time as HH:MM, like 14:05.'
        : at.getTime() > now.getTime() + 5 * 60_000
          ? 'The time is in the future.'
          : null,
    location: location.trim() === '' ? 'Say where it happened.' : null,
    description: description.trim() === '' ? 'Say what happened.' : null,
  };
  const valid = Object.values(problems).every((p) => p === null);

  const submit = useMutation({
    mutationFn: () =>
      reportIncident({
        clientRequestId,
        categoryCode: category ?? '',
        severityCode: severity ?? '',
        occurredAt: (at ?? new Date()).toISOString(),
        location: location.trim(),
        description: description.trim(),
        immediateAction: immediateAction.trim() === '' ? null : immediateAction.trim(),
        equipmentText: equipment.trim() === '' ? null : equipment.trim(),
        patientId: patient?.id ?? null,
        visitId,
        anonymous,
      }),
    onSuccess: (incident) => {
      void queryClient.invalidateQueries({ queryKey: incidentKeys.all });
      toasts.show({ title: `Incident reported, ${incident.incidentNumber}` });
      void navigate({ to: '/incidents/$incidentId', params: { incidentId: incident.id } });
    },
  });

  if (options.isPending) return <LoadingBlock label="Loading the form" rows={6} />;
  if (options.isError)
    return (
      <LoadFailed
        error={options.error}
        fallback="The form couldn't be loaded. Try again."
        onRetry={() => void options.refetch()}
      />
    );
  const show = (problem: string | null) =>
    tried && problem !== null ? { errorMessage: problem } : {};
  return (
    <form
      className={styles.form}
      aria-label="Report an incident"
      onSubmit={(event) => {
        event.preventDefault();
        setTried(true);
        if (valid) submit.mutate();
      }}
    >
      <Section title="What happened">
        <div className={styles.form}>
          <div className={styles.fields}>
            <Select
              label="Category"
              placeholder="Choose one"
              options={options.data.categories
                .filter((c) => c.active)
                .map((c) => ({ id: c.code, label: c.display }))}
              value={category}
              onChange={(key) => {
                setCategory(key === null ? null : String(key));
              }}
              {...show(problems.category)}
            />
            <Select
              label="Harm"
              placeholder="Choose one"
              description="The harm done, as far as you know now."
              options={options.data.severities
                .filter((s) => s.active)
                .map((s) => ({ id: s.code, label: s.display }))}
              value={severity}
              onChange={(key) => {
                setSeverity(key === null ? null : String(key));
              }}
              {...show(problems.severity)}
            />
          </div>
          <div className={styles.row}>
            <DateField
              label="Day it happened"
              value={day}
              onChange={setDay}
              maxValue={formatIsoDay(eatDayOf(now))}
              isRequired
            />
            <TextField
              label="Time"
              description="24-hour clock, for example 14:05."
              width="sm"
              value={time}
              onChange={setTime}
              isRequired
              {...show(problems.at)}
            />
          </div>
          <TextField
            label="Where"
            description="The ward, room or place, such as Ward 2 corridor."
            value={location}
            onChange={setLocation}
            maxLength={L.location}
            {...show(problems.location)}
          />
          <TextArea
            label="What happened"
            description="Facts only: what you saw and heard. Don't name colleagues."
            value={description}
            onChange={setDescription}
            rows={5}
            maxLength={L.description}
            {...show(problems.description)}
          />
          <TextArea
            label="What was done straight away (optional)"
            value={immediateAction}
            onChange={setImmediateAction}
            rows={3}
            maxLength={L.immediateAction}
          />
        </div>
      </Section>
      <Section
        title="Who and what was involved"
        description="Optional. A linked patient makes this report part of their record's audit."
      >
        <div className={styles.form}>
          {mayLinkPatient ? (
            <PatientLink
              patient={patient}
              visitId={visitId}
              onChange={(p, v) => {
                setPatient(p);
                setVisitId(v);
              }}
            />
          ) : (
            <p className={styles.meta}>
              You can&apos;t open patient records, so you can&apos;t link a patient. Describe them
              in what happened, without their name.
            </p>
          )}
          <TextField
            label="Equipment (optional)"
            description="The device or item involved, such as Infusion pump 3."
            value={equipment}
            onChange={setEquipment}
            maxLength={L.equipmentText}
          />
        </div>
      </Section>
      <Section title="Your name">
        <Checkbox
          isSelected={anonymous}
          onChange={setAnonymous}
          description="Your name is still stored with the report, and the quality and safety lead can see it."
        >
          Hide my name from colleagues
        </Checkbox>
      </Section>
      {submit.isError ? (
        <Notice tone="error">
          {errorText(submit.error, "The report wasn't sent. Try again.")}
        </Notice>
      ) : null}
      <div className={styles.actions}>
        <Button
          type="submit"
          variant="primary"
          isDisabled={!online}
          isPending={submit.isPending}
          pendingLabel="Reporting…"
        >
          Report incident
        </Button>
        {online ? null : (
          <p className={styles.meta}>Reporting works again when you&apos;re back online.</p>
        )}
      </div>
    </form>
  );
}

export function ReportIncidentPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  if (session === null || !can(session, 'incidents.report'))
    return <NotAllowed title="Report an incident" what="report incidents" />;
  return (
    <div className={styles.page}>
      <PageHeader
        title="Report an incident"
        description="Anything that harmed, or could have harmed, a patient, a visitor or a member of staff."
      />
      <p className={styles.meta}>
        <Link to="/incidents">Incidents</Link>
      </p>
      <ReportForm mayLinkPatient={can(session, 'patients.read')} />
    </div>
  );
}
